import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { PriorityBadge, StatusBadge, Avatar } from "../components/Badges";
import { STATUSES, fmtDate } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import {
  Layers, Clock, Search, Code2, TestTube, Rocket, CheckCircle2, AlertTriangle, Flame,
  Lock, UserX, Zap, TrendingUp,
} from "lucide-react";

const METRIC_DEFS = [
  { key: "total", label: "Total", icon: Layers, color: "#0A0A0A" },
  { key: "pendientes", label: "Pendientes", icon: Clock, color: "#64748B" },
  { key: "en_analisis", label: "En análisis", icon: Search, color: "#2563EB" },
  { key: "en_desarrollo", label: "En desarrollo", icon: Code2, color: "#4F46E5" },
  { key: "en_pruebas", label: "En pruebas", icon: TestTube, color: "#9333EA" },
  { key: "listo_deploy", label: "Listos Deploy", icon: Rocket, color: "#0D9488" },
  { key: "terminados", label: "Terminados", icon: CheckCircle2, color: "#16A34A" },
  { key: "atrasados", label: "Atrasados", icon: AlertTriangle, color: "#DC2626" },
];

const ATTENTION = [
  { key: "overdue", label: "Fecha vencida", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
  { key: "urgent_not_started", label: "Crítico sin iniciar", icon: Zap, color: "text-orange-600", bg: "bg-orange-50 border-orange-200" },
  { key: "stale", label: "Sin movimiento", icon: Clock, color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200" },
  { key: "overloaded", label: "Dev sobrecargado", icon: UserX, color: "text-rose-600", bg: "bg-rose-50 border-rose-200" },
];

const STATUS_COLORS = {
  pendiente: "#64748B", en_analisis: "#2563EB", en_desarrollo: "#4F46E5", en_pruebas: "#9333EA",
  correccion: "#EA580C", aprobado: "#059669", listo_deploy: "#0D9488", produccion_pendiente_firma: "#22C55E", en_produccion: "#16A34A", pendiente_info_cliente: "#D97706", cancelado: "#A1A1AA",
};

function Metric({ def, value }) {
  return (
    <div data-testid={`metric-${def.key}`} className="border-r border-b border-border bg-white p-5 hover:bg-slate-50 transition-colors duration-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{def.label}</span>
        <def.icon className="h-4 w-4" style={{ color: def.color }} />
      </div>
      <div className="font-heading text-4xl font-extrabold tracking-tighter" style={{ color: def.color }}>{value ?? 0}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState(null);

  const load = () => {
    Promise.all([api.get("/metrics"), api.get("/alerts")]).then(([m, a]) => {
      setMetrics(m.data);
      setAlerts(a.data);
    });
  };
  useEffect(() => { load(); }, []);

  const statusChart = useMemo(() => {
    if (!metrics) return [];
    return Object.entries(metrics.by_status)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: STATUSES[k]?.label || k, value: v, key: k }));
  }, [metrics]);

  if (!metrics) return <div className="p-10 text-slate-400">Cargando...</div>;

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b border-border px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Dashboard Ejecutivo</p>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5">Hola, {user?.name?.split(" ")[0]} 👋</h1>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">Avance general</div>
              <div className="font-heading text-2xl font-extrabold">{metrics.avance_general}%</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">Críticos activos</div>
              <div className="font-heading text-2xl font-extrabold text-red-600 flex items-center gap-1 justify-end"><Flame className="h-5 w-5" />{metrics.criticos}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
        {METRIC_DEFS.map((d) => <Metric key={d.key} def={d} value={metrics[d.key]} />)}
      </div>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Attention panel */}
        <div className="lg:col-span-2">
          <div className="border-2 border-red-200 bg-white rounded-sm">
            <div className="bg-red-50 border-b border-red-200 px-5 py-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="font-heading font-bold text-red-900">Atención Inmediata</span>
              <span className="ml-auto font-mono text-xs bg-red-600 text-white px-2 py-0.5 rounded-sm" data-testid="attention-count">{alerts?.total || 0}</span>
            </div>
            <div className="p-5 space-y-5">
              {ATTENTION.map((a) => {
                const items = alerts?.[a.key] || [];
                if (items.length === 0) return null;
                return (
                  <div key={a.key} data-testid={`attention-${a.key}`}>
                    <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wide mb-2 ${a.color}`}>
                      <a.icon className="h-3.5 w-3.5" /> {a.label} <span className="font-mono">({items.length})</span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((it) => (
                        <div key={it.id} className={`flex items-center justify-between border rounded-sm px-3 py-2 ${a.bg}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-[10px] text-slate-500">{it.dev_id}</span>
                            <span className="text-sm font-medium truncate">{it.title}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <PriorityBadge priority={it.priority} />
                            {it.fecha_compromiso && <span className="font-mono text-[10px] text-slate-500">{fmtDate(it.fecha_compromiso)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {(!alerts || alerts.total === 0) && (
                <div className="text-center py-8 text-slate-400 flex flex-col items-center gap-2">
                  <CheckCircle2 className="h-8 w-8 text-green-400" />
                  Todo bajo control. Sin alertas activas.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Side stats */}
        <div className="space-y-6">
          <div className="border border-border bg-white rounded-sm p-5">
            <h3 className="font-heading font-bold text-lg mb-4">Horas del proyecto</h3>
            {[
              { l: "Estimadas", v: metrics.horas_estimadas, c: "#0A0A0A" },
              { l: "Consumidas", v: metrics.horas_consumidas, c: "#EA580C" },
              { l: "Restantes", v: metrics.horas_restantes, c: "#16A34A" },
            ].map((h) => (
              <div key={h.l} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-600">{h.l}</span>
                <span className="font-mono font-semibold" style={{ color: h.c }}>{h.v}h</span>
              </div>
            ))}
          </div>

          <div className="border border-border bg-white rounded-sm p-5">
            <div className="flex items-center gap-2 mb-1"><TrendingUp className="h-4 w-4 text-green-600" /><h3 className="font-heading font-bold text-lg">Esta semana</h3></div>
            <div className="font-heading text-4xl font-extrabold text-green-600 mt-2">{metrics.terminados_semana}</div>
            <p className="text-sm text-slate-500">desarrollos en producción</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="px-8 pb-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="border border-border bg-white rounded-sm p-5">
          <h3 className="font-heading font-bold text-lg mb-4">Distribución por estado</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusChart} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#F4F4F5" }} />
              <Bar dataKey="value" radius={0}>
                {statusChart.map((e) => <Cell key={e.key} fill={STATUS_COLORS[e.key] || "#0A0A0A"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-white rounded-sm p-5">
          <h3 className="font-heading font-bold text-lg mb-4">Productividad por desarrollador</h3>
          <div className="space-y-3">
            {metrics.productividad.map((p) => (
              <div key={p.name} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                <span className="text-sm font-medium">{p.name}</span>
                <div className="flex items-center gap-4 font-mono text-xs text-slate-500">
                  <span>{p.total} tareas</span>
                  <span className="text-green-600">{p.terminados} listos</span>
                  <span>{p.horas}h</span>
                </div>
              </div>
            ))}
            {metrics.productividad.length === 0 && <p className="text-slate-400 text-sm">Sin datos.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
